<template>
    <button type="button" class="inspection-photo" :style="{width: w + 'px', height: h + 'px'}"
            :aria-label="label" @click="emit('open', path)">
        <v-img v-if="url" :src="url" :width="w" :height="h" :cover="!contain" :alt="label"/>
        <v-icon v-else-if="failed" size="20" class="text-medium-emphasis">mdi-image-off-outline</v-icon>
        <v-progress-circular v-else indeterminate size="18" width="2"/>
    </button>
</template>

<script setup>
import {computed, onBeforeUnmount, ref, watch} from "vue";
import {send} from "@/composables/apiClient";

// A thumbnail of an inspection photo or signature, fetched with the session token.
const props = defineProps({
    path: {type: String, required: true},
    size: {type: Number, default: 56},
    width: {type: Number, default: null},
    height: {type: Number, default: null},
    contain: {type: Boolean, default: false},
    label: {type: String, default: "Open photo"},
});
const emit = defineEmits(["open"]);

const w = computed(() => props.width ?? props.size);
const h = computed(() => props.height ?? props.size);

const url = ref("");
const failed = ref(false);
let seq = 0;

const free = () => {
    if (url.value) URL.revokeObjectURL(url.value);
    url.value = "";
};

watch(() => props.path, async (path) => {
    const mine = ++seq;
    free();
    failed.value = false;
    if (!path) return;
    try {
        const res = await send("get", "fleet/inspection-photo", {path}, {responseType: "blob"});
        if (mine === seq) url.value = URL.createObjectURL(res.data);
    } catch {
        if (mine === seq) failed.value = true;
    }
}, {immediate: true});

onBeforeUnmount(() => {
    seq++;
    free();
});
</script>

<style scoped>
.inspection-photo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    background: rgb(var(--v-theme-surface));
    cursor: zoom-in;
}
</style>
